import React, { useState } from "react";
import {
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Box,
  CircularProgress,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { generateCustomerBackupPdf } from "../../services/invoiceService"; // 🔁 Adjust path as needed

const CustomerBackupDownloader = () => {
  const [backupUrl, setBackupUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleGenerateCustomerBackup = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await generateCustomerBackupPdf();
      if (response.success && response.url) {
        setBackupUrl(response.url);
      } else {
        setError("Customer backup generation failed. Please try again.");
      }
    } catch (err) {
      setError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(backupUrl);
      const blob = await response.blob();
      const file = new Blob([blob], { type: "application/pdf" });
      const fileURL = URL.createObjectURL(file);

      const a = document.createElement("a");
      a.href = fileURL;
      a.download = `Customer-Backup-${new Date().toLocaleDateString(
        "en-IN"
      )}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Download failed. Try again.");
    }
  };

  return (
    <div className="max-w-md mx-auto p-4">
      <h1 className="text-xl font-bold text-center mb-4 text-gray-800">
        🧾 Customer Backup PDF Generator
      </h1>

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
          <svg
            className="w-5 h-5 mr-2 text-red-600"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
          >
            <path d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      {/* Action Card */}
      <div className="bg-white shadow-lg rounded-xl p-5">
        <p className="text-gray-700 mb-4">
          Generate and download a complete customer backup (name, mobile,
          address) as PDF.
        </p>

        <button
          onClick={handleGenerateCustomerBackup}
          disabled={loading}
          className={`w-full py-3 rounded-lg font-semibold text-white transition text-lg ${
            loading
              ? "bg-gray-400 cursor-not-allowed"
              : "bg-blue-600 hover:bg-blue-700"
          }`}
        >
          {loading ? (
            <div className="flex flex-col items-center justify-center">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-white animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
                <span className="text-white font-semibold">
                  Generating PDF...
                </span>
              </div>
              <span className="text-xs text-white mt-1 italic">
                Please wait, preparing your file
              </span>
            </div>
          ) : (
            "📥 Generate Customer Backup"
          )}
        </button>
      </div>

      {/* Download Card */}
      {backupUrl && (
        <div className="bg-green-100 border border-green-300 mt-6 p-4 rounded-lg text-center shadow-md">
          <h2 className="text-lg font-semibold text-green-800 mb-2">
            ✅ Backup Ready!
          </h2>
          <p className="text-gray-700 mb-3">
            Click below to download your PDF backup.
          </p>

          <button
            onClick={handleDownload}
            className="w-full py-3 rounded-lg bg-green-600 text-white font-bold hover:bg-green-700 transition"
          >
            ⬇️ Download Backup PDF
          </button>
        </div>
      )}
    </div>
  );
};

export default CustomerBackupDownloader;
